import * as _ from 'lodash';

import { secondsToTimeString } from '@datetime';
import { Unit } from './shared';
import { VersionedEntityOperationType, VersionedEntityType } from './versions';

/**
 * TEMPLATES
 */
enum ActivationTokenType {
  TEXT = 'TEXT',
  TASK = 'TASK',
  TAG = 'TAG',
  OPERATOR = 'OPERATOR',
  VARIABLE = 'VARIABLE',
  VALUE = 'VALUE'
}

enum ActivationTokenOperatorType {
  EQ = 'EQ',
  GTE = 'GTE',
  GT = 'GT',
  LTE = 'LTE',
  LT = 'LT'
}

enum ActivationPhraseTemplateType {
  TASK = 'TASK',
  TAG = 'TAG',
  WEATHER = 'WEATHER',
  TIME = 'TIME',
  GROUP = 'GROUP'
}

enum ActivationTokenValueType {
  INTEGER = 'INTEGER',
  TIME = 'TIME'
}

enum ActivationPhraseScopeType {
  GLOBAL = 'GLOBAL',
  CURRENT_PATH = 'CURRENT_PATH'
}

enum ActivationParentType {
  PATH = 'PATH',
  TASK = 'TASK',
  ACTION = 'ACTION',
  MESSAGE_TEMPLATE = 'MESSAGE_TEMPLATE',
  GROUP = 'GROUP'
}

interface BaseActivationTokenModel {
  type: ActivationTokenType;
  label: string | null;
  arg: string | null;
}

interface ActivationTaskTokenModel extends BaseActivationTokenModel {}

interface ActivationTagTokenModel extends BaseActivationTokenModel {}

interface ActivationTextTokenModel extends BaseActivationTokenModel {}

interface ActivationVariableTokenModel extends BaseActivationTokenModel {
  varName: string;
}

interface ActivationOperatorTokenChoiceModel {
  operator: ActivationTokenOperatorType;
  label: string | null;
}

interface ActivationOperatorTokenModel extends BaseActivationTokenModel {
  operator: ActivationTokenOperatorType | null;
  choices: ActivationOperatorTokenChoiceModel[];
}

interface ActivationValueTokenChoiceModel {
  exact: number | null;
  label: string | null;
}

interface ActivationValueTokenModel extends BaseActivationTokenModel {
  valueType: ActivationTokenValueType;
  exact: number | null;
  min: number | null;
  max: number | null;
  unit: Unit | null;
  choices: ActivationValueTokenChoiceModel[];
}
interface ActivationPhraseTemplateModel {
  entityId: string;
  type: ActivationPhraseTemplateType;
  allowedScopes: ActivationPhraseScopeType[];
  tokens: (
    | ActivationTaskTokenModel
    | ActivationTagTokenModel
    | ActivationTextTokenModel
    | ActivationVariableTokenModel
    | ActivationOperatorTokenModel
    | ActivationValueTokenModel
  )[];
}

const getActivationTokenLabel = (
  token:
    | ActivationTaskTokenModel
    | ActivationTagTokenModel
    | ActivationTextTokenModel
    | ActivationVariableTokenModel
    | ActivationOperatorTokenModel
    | ActivationValueTokenModel,
  translator: (key: string, args?: { [key: string]: string | number }) => string
): string => {
  let t = null;
  switch (token.type) {
    case ActivationTokenType.TEXT:
      return token.label;

    case ActivationTokenType.VARIABLE:
      return token.label;

    case ActivationTokenType.OPERATOR:
      t = token as ActivationOperatorTokenModel;
      return t.choices.length > 1 ? t.choices[0].label : token.label;

    case ActivationTokenType.TAG:
      return translator('activations.tagNamePlaceholder');

    case ActivationTokenType.TASK:
      return translator('activations.taskNamePlaceholder');

    case ActivationTokenType.VALUE:
      t = token as ActivationValueTokenModel;
      if (t.choices.length > 1) {
        return t.choices[0].label;
      }

      let value = 0;
      if (t.exact) {
        value = t.exact;
      } else if (!_.isNil(t.min) && !_.isNil(t.max)) {
        const span = t.max - t.min;
        value = t.min + Math.round(span * 0.2);
      } else if (!_.isNil(t.min)) {
        value = t.min;
      } else if (!_.isNil(t.max)) {
        value = t.max;
      }

      let label = '';
      if (t.valueType === ActivationTokenValueType.TIME) {
        label = secondsToTimeString(value, true);
      } else {
        label = value.toString();
      }

      if (!label) {
        label = t.label;
      }

      return label;
  }
};

const getActivationTokenDefaultValue = (
  token:
    | ActivationTaskTokenModel
    | ActivationTagTokenModel
    | ActivationTextTokenModel
    | ActivationVariableTokenModel
    | ActivationOperatorTokenModel
    | ActivationValueTokenModel,
  tagEntityIds: string[],
  taskEntityIds: string[]
): number | string => {
  let t = null;
  switch (token.type) {
    case ActivationTokenType.OPERATOR:
      t = token as ActivationOperatorTokenModel;
      return t.choices.length > 1 ? t.choices[0].operator : null;

    case ActivationTokenType.TAG:
      return tagEntityIds.length > 0 ? tagEntityIds[0] : null;

    case ActivationTokenType.TASK:
      return taskEntityIds.length > 0 ? taskEntityIds[0] : null;

    case ActivationTokenType.VALUE:
      t = token as ActivationValueTokenModel;
      if (t.choices.length > 0) {
        return t.choices[0].exact;
      }

      let value = 0;
      if (t.exact) {
        value = t.exact;
      } else if (!_.isNil(t.min) && !_.isNil(t.max)) {
        const span = t.max - t.min;
        value = t.min + Math.round(span * 0.2);
      } else if (!_.isNil(t.min)) {
        value = t.min;
      } else if (!_.isNil(t.max)) {
        value = t.max;
      }

      return value;
  }
};

/**
 * RULES
 */
enum ActivationOperatorType {
  AND = 'AND',
  OR = 'OR'
}

enum ActivationRuleItemType {
  OPERATOR = 'OPERATOR',
  PHRASE = 'PHRASE'
}

interface CreateOrUpdateActivationPhraseModel {
  templateEntityId: string;
  args: { [name: string]: number | string };
}

interface CreateOrUpdateActivationRuleModel {
  name: string;
  parentType: ActivationParentType;
  items: {
    type: ActivationRuleItemType;
    phrase: CreateOrUpdateActivationPhraseModel | null;
    operator: ActivationOperatorType | null;
  }[];
}

interface ActivationPhraseModel {
  templateEntityId: string;
  template: ActivationPhraseTemplateModel | null;
  args: { [name: string]: number | string };
}

interface ActivationRuleModel {
  // version & identity
  entityId: string | null;
  versionType: VersionedEntityType;
  operationType: VersionedEntityOperationType;
  createdAt: string;
  updatedAt: string;

  // own fields
  parentType: ActivationParentType;
  name: string;
  items: {
    type: ActivationRuleItemType;
    phrase: ActivationPhraseModel | null;
    operator: ActivationOperatorType | null;
  }[];
}

export {
  ActivationOperatorType,
  ActivationParentType,
  ActivationPhraseScopeType,
  ActivationPhraseTemplateType,
  ActivationRuleItemType,
  ActivationTokenOperatorType,
  ActivationTokenType,
  ActivationTokenValueType,
  getActivationTokenDefaultValue,
  getActivationTokenLabel
};
export type {
  ActivationOperatorTokenModel,
  ActivationPhraseModel,
  ActivationPhraseTemplateModel,
  ActivationRuleModel,
  ActivationTagTokenModel,
  ActivationTaskTokenModel,
  ActivationTextTokenModel,
  ActivationValueTokenModel,
  ActivationVariableTokenModel,
  CreateOrUpdateActivationRuleModel
};
